import { Screen } from '@/components/layout'
import { Button } from '@/components/primitives'
import { ErrorState, Skeleton } from '@/components/states'
import { ApiFailure, probeServer } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

/**
 * First run. The only work it does is confirm the Server answers, so
 * `welcome.error` means something real rather than decorating a spinner.
 */
export function WelcomeScreen() {
  const navigate = useNavigate()

  const { state, reload } = useAsync(async () => {
    const reachable = await probeServer()
    if (!reachable) {
      throw new ApiFailure({ code: 'serverError', copyKey: 'error.network', retryable: true }, 0)
    }
    return true
  }, [])

  const screenState = state.status === 'loading' ? 'loading' : state.status === 'error' ? 'error' : 'ready'

  return (
    <Screen id="welcome" state={screenState} chrome="standalone">
      <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <HeroImage />
        {/* The logo above already sets the name in type, so rendering it again
            as text would say DimeRoot twice. The h1 stays for the document
            outline and for screen readers, which cannot read the wordmark. */}
        <h1 className="visually-hidden">{t('welcome.title')}</h1>
        <p className="helper">{t('welcome.tagline')}</p>

        {screenState === 'loading' ? (
          <div className="stack-sm" style={{ width: '100%' }} aria-busy="true">
            <Skeleton height="var(--layout-minTapTarget)" />
            <Skeleton height="var(--layout-minTapTarget)" />
          </div>
        ) : screenState === 'error' ? (
          <ErrorState
            titleKey="welcome.error"
            error={state.status === 'error' ? state.error : undefined}
            onRetry={reload}
          />
        ) : (
          <div className="stack-sm" style={{ width: '100%' }}>
            {/* One button, because there was only ever one destination. "Get
                started" and "Sign in" both called navigate('signIn') — the
                pair implied a new-user path and a returning-user path, but
                there is no signup: findOrCreateByEmail makes the account on
                first sign-in. Offering a choice that isn't one just asks the
                reader to work out which button they are. */}
            <Button
              variant="primary"
              block
              onClick={() => navigate('signIn', {}, { query: { next: 'upload' } })}
            >
              {t('welcome.cta.primary')}
            </Button>
          </div>
        )}
      </div>
    </Screen>
  )
}

/**
 * The full DimeRoot logo — mark and wordmark together.
 *
 * Cropped from the lockup above its divider: the shipped lockup ends with
 * "GROW YOUR WEALTH. BUILD YOUR FUTURE.", which would sit directly on top of
 * `welcome.tagline` and give the screen two competing taglines. The copy
 * contract owns the tagline, so the artwork should not also carry one.
 *
 * Decorative here, because the h1 beneath carries the name for assistive tech.
 */
function HeroImage() {
  return (
    <img
      src="/dimeroot-logo.png"
      alt=""
      aria-hidden="true"
      width={996}
      height={571}
      style={{
        // Bounded by the column, not by a fixed width, so it holds up from a
        // phone to the desktop max-width without a media query.
        width: 'min(100%, calc(var(--space-xxl) * 4))',
        height: 'auto',
      }}
    />
  )
}
